import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Grid, Card, Button, Box, Typography, Divider, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import LoaderOverlay from "components/LoaderOverlay";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { api } from "../../api";
import ProfileSection from "./ProfileSection";

function PatientDetails() {
  const { patient_code, couple_code } = useParams();
  const navigate = useNavigate();

  // State management
  const [patientData, setPatientData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Load patient details on component mount
  useEffect(() => {
    const loadPatientDetails = async () => {
      if (!patient_code) {
        setError("Patient code is required");
        return;
      }

      setLoading(true);
      setError("");

      try {
        // Call the patient details API endpoint
        const response = await api.contacts.getPatientDetails(patient_code);

        if (response?.data) {
          setPatientData(response.data);
        } else {
          setError("Patient details not found");
        }
      } catch (error) {
        console.error("Error loading patient details:", error);
        setError(error.response?.data?.message || "Failed to load patient details");
      } finally {
        setLoading(false);
      }
    };

    loadPatientDetails();
  }, [patient_code]);

  const handleGoBack = () => {
    navigate("/patient-list");
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <LoaderOverlay open={loading} />
        <MDBox pt={6} pb={3}>
          <Typography variant="h6">Loading patient details...</Typography>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="h6" color="error" gutterBottom>
                  {error}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleGoBack}
                  sx={{ mt: 2 }}
                >
                  Back to Patient List
                </Button>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          {/* Header with Back Button */}
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" mb={2}>
              <IconButton
                onClick={handleGoBack}
                sx={{
                  mr: 2,
                  color: "#00798E",
                  "&:hover": { backgroundColor: "rgba(0, 121, 142, 0.04)" },
                }}
              >
                <ArrowBackIcon />
              </IconButton>
              <MDTypography variant="h4" fontWeight="medium">
                Patient Details
              </MDTypography>
            </Box>
          </Grid>

          {/* Patient Information Cards */}
          {patientData && (
            <>
              {/* Render Patient Profile */}
              <ProfileSection
                person={patientData.contact}
                title="Patient Information"
                isSpouse={false}
                patientData={patientData}
              />

              {/* Render Spouse Profile if available */}
              {patientData.spouse && patientData.spouse?.contact && (
                <ProfileSection
                  person={patientData.spouse?.contact}
                  title="Spouse Information"
                  isSpouse={true}
                  patientData={patientData.spouse}
                />
              )}
            </>
          )}

          {!patientData && (
            <Grid item xs={12}>
              <Card sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body1">No patient data available</Typography>
              </Card>
            </Grid>
          )}

          {/* Additional Information Sidebar - Only show if patient data exists */}
          {patientData && (
            <Grid item xs={12} md={4} lg={4}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="success"
                  borderRadius="lg"
                  coloredShadow="success"
                >
                  <MDTypography variant="h6" color="white">
                    Couple Information
                  </MDTypography>
                </MDBox>

                <MDBox p={3}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Patient Code
                      </Typography>
                      <Typography variant="body1" fontWeight="bold" mb={2}>
                        {patient_code}
                      </Typography>
                    </Grid>

                    {couple_code && couple_code !== "undefined" && (
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Couple Code
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" mb={2}>
                          {couple_code}
                        </Typography>
                      </Grid>
                    )}

                    {patientData.marital_status && (
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Marital Status
                        </Typography>
                        <Typography variant="body1" fontWeight="medium" mb={2}>
                          {patientData.marital_status}
                        </Typography>
                      </Grid>
                    )}

                    {patientData.religion && (
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Religion
                        </Typography>
                        <Typography variant="body1" fontWeight="medium" mb={2}>
                          {patientData.religion}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<ArrowBackIcon />}
                    onClick={handleGoBack}
                    sx={{
                      backgroundColor: "#00798E",
                      "&:hover": { backgroundColor: "#005f6b" },
                    }}
                  >
                    Back to Patient List
                  </Button>
                </MDBox>
              </Card>
            </Grid>
          )}
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default PatientDetails;
